import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Table, Image, Spinner, Button } from 'react-bootstrap';
import { ToastContext } from './context/ToastContext';
import getSymbolFromCurrency from 'currency-symbol-map';
import { LoadingContext } from './context/LoadingContext';
import { buildProducts } from './ProductList';
import AppContext from './context/AppContext';
import Breadcrumbs from './Breadcrumbs';

const CompareProduct = () => {
    const [productList, setProductList] = useState([]);
    const [loading, setLoading] = useState(true);
    const { sendMessage } = useContext(ToastContext);
    const { startLoading, finishLoading } = useContext(LoadingContext);
    const { appContext } = useContext(AppContext);
    const appConfig = appContext.config.themeConfig;
    const { search } = useLocation();

    const params = new URLSearchParams(search);
    const productIds = params.get('ids');
    const source = params.get('source');
    const path = params.get('path');

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            startLoading();
            const query = new URLSearchParams({ productIds }).toString();
            const res = await fetch(new URL('api/products?' + query, process.env.APP_URL));
            const data = await res.json();
            setProductList(buildProducts(data.products ?? []));
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
            finishLoading();
        }
    }, [productIds]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleRemove = useCallback((idToRemove) => {
        if (productList.length <= 2) {
            sendMessage('You must compare at least 2 products.', 'danger');
            return;
        }
        const updatedList = productList.filter((p) => p.id !== idToRemove);
        setProductList(updatedList);
    }, [productList, sendMessage]);

    const handleRemoveClick = (id) => () => {
        handleRemove(id);
    };

    return (
        <Container
            className={'my-4'}>
            <Breadcrumbs
                breadcrumbList={[
                    [source, path],
                    ['Compare Products', '/#']
                ]} />
            <h4
                className={'mb-4'}>Product Comparison</h4>

            {loading ? (
                <div
                    className={'text-center my-5'}>
                    <Spinner
                        animation={'border'} />
                </div>
            ) : (
                <Table

                    responsive>
                    <thead>
                        <tr>
                            <th
                                style={{
                                    whiteSpace: 'nowrap',
                                }}>Product Details</th>
                            {productList.map((product) => (
                                <th
                                    key={product.id}>
                                    <div
                                        className={'d-flex justify-content-center align-items-center'}>
                                        <Button
                                            variant={'link'}
                                            onClick={handleRemoveClick(product.id)}
                                            className={'text-danger p-0'}>Remove</Button>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><b>Product</b></td>
                            {productList.map((product) => (
                                <td
                                    key={product.id}>
                                    {product.title ?? 'N/A'}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td><b>Image</b></td>
                            {productList.map((product) => (
                                <td
                                    key={product.id}>
                                    {product.imageUrl ? (
                                        <Image
                                            src={product.imageUrl}
                                            fluid
                                            alt={product.name}
                                        />
                                    ) : (
                                        'N/A'
                                    )}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td><b>Price</b></td>
                            {productList.map((product) => (
                                <td
                                    key={product.id}>
                                    <strong>{getSymbolFromCurrency(appConfig.storeCurrency)} {product.price?.toFixed(2)} {appConfig.storeCurrency}</strong>
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td><b>Brand</b></td>
                            {productList.map((product) => (
                                <td
                                    key={product.id}>{product.vendor ?? 'N/A'}</td>
                            ))}
                        </tr>
                        <tr>
                            <td><b>Availability</b></td>
                            {productList.map((product) => (
                                <td
                                    key={product.id}>{product.availableForSale ? 'Available' : 'Not Available'}</td>
                            ))}
                        </tr>
                        <tr>
                            <td><b>Rating</b></td>
                            {productList.map((product) => (
                                <td
                                    key={product.id}
                                    className={'text-muted'}>
                                    <div
                                        className={'d-flex justify-content-start'}>
                                        {product.avgRating || <div
                                            style={{
                                                fontSize: '12px',
                                            }}
                                            className={'border p-1 text-muted'}>NOT RATED</div>}
                                    </div>
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td><b>Summary</b></td>
                            {productList.map((product) => (
                                <td
                                    key={product.id}
                                    className={'small text-muted'}>
                                    {product.description || 'N/A'}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td><b>Other</b></td>
                            {productList.map((product) => (
                                <td
                                    key={product.id}
                                    className={'small text-muted'}>
                                    <div><strong>Part Number:</strong> {product.partNumber ?? 'N/A'}</div>
                                    <div><strong>Condition:</strong> {product.condition ?? 'N/A'}</div>
                                    <div><strong>Instock:</strong> {product.availableQuantity > 0 ? 'Yes' : 'No'}</div>
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </Table>
            )
            }
        </Container >
    );
};

export default CompareProduct;
